import axios from "axios";

const apiBaseUrl =
  process.env.REACT_APP_API_BASE_URL?.replace(/\/+$/, "") || "http://localhost:8000";

const api = axios.create({
  baseURL: apiBaseUrl,
  withCredentials: true, // send the httpOnly refresh-token cookie
});

let accessToken = null;
let unauthorizedHandler = () => {};

export function setAccessToken(token) {
  accessToken = token;
}

export function setUnauthorizedHandler(handler) {
  unauthorizedHandler = handler;
}

api.interceptors.request.use((config) => {
  if (accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  }
  return config;
});

let refreshPromise = null;

// Shared by the interceptor below and AuthContext's mount-time session
// restore, so there is only ever one in-flight /auth/refresh call across
// the whole app - two independent callers racing each other is exactly
// what causes an already-rotated refresh token to get presented again.
export function refreshSession() {
  if (!refreshPromise) {
    refreshPromise = api.post("/auth/refresh").finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
}

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const { config, response } = error;
    const isAuthEndpoint = config?.url === "/auth/refresh" || config?.url === "/auth/login";

    if (response?.status === 401 && config && !config._retry && !isAuthEndpoint) {
      config._retry = true;
      try {
        const { data } = await refreshSession();
        setAccessToken(data.access_token);
        config.headers.Authorization = `Bearer ${data.access_token}`;
        return api(config);
      } catch (refreshError) {
        setAccessToken(null);
        unauthorizedHandler();
        return Promise.reject(error);
      }
    }

    return Promise.reject(error);
  }
);

export default api;
